import React, { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { Megaphone, MapPin, Send, AlertTriangle, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { getProperties } from "@/lib/api";
import { toast } from "sonner";
import { getCurrentUser } from "@/lib/store";

export const AdminBroadcast = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [broadcastType, setBroadcastType] = useState("event");
  const [eventName, setEventName] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      if (user.role === "global" || user.role === "admin") {
        setIsGlobalAdmin(true);
        fetchProperties();
      } else {
        setIsGlobalAdmin(false);
        setSelectedProperty(user.property_id || "");
      }
    }
  }, []);

  const fetchProperties = async () => {
    try {
      const data = await getProperties();
      if (data && data.properties) {
        setProperties(data.properties.filter(p => p.id !== "global" && p.id !== "temp"));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!selectedProperty || selectedProperty === "global") {
      toast.error("Please select a specific property to broadcast to.");
      return;
    }

    if (broadcastType === "event" && (!eventName || !eventTime)) {
      toast.error("Event name and time are required.");
      return;
    }
    if (broadcastType === "alert" && !alertMessage) {
      toast.error("Alert message is required.");
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to send this broadcast to all active guests at ${selectedProperty}?`);
    if (!confirmed) return;

    setLoading(true);
    try {
      const payload = {
        property_id: selectedProperty,
        broadcast_type: broadcastType,
        event_name: eventName,
        event_time: eventTime,
        alert_message: alertMessage
      };

      const res = await api.post("/admin/broadcast", payload);
      const data = res.data;

      if (data.success) {
        toast.success(data.message || `Sent to ${data.count} guests successfully!`);
        setEventName("");
        setEventTime("");
        setAlertMessage("");
      } else {
        toast.error(data.detail || data.message || "Failed to send broadcast");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send broadcast";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Guest Broadcasts">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 border-b pb-4 mb-6">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <Megaphone className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">New Broadcast</h2>
              <p className="text-sm text-gray-500">Send WhatsApp announcements to currently checked-in guests.</p>
            </div>
          </div>

          <form onSubmit={handleSend} className="space-y-6">
            {isGlobalAdmin && (
              <div className="space-y-2">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" /> Target Property
                </label>
                <select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  required
                >
                  <option value="">Select a property...</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">You must explicitly choose which property to broadcast to.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-semibold">Broadcast Type</label>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  onClick={() => setBroadcastType("event")}
                  className={`p-4 border rounded-xl cursor-pointer flex items-center gap-3 transition-colors ${broadcastType === "event" ? "border-amber-500 bg-amber-50" : "hover:bg-gray-50"}`}
                >
                  <PartyPopper className={`w-5 h-5 ${broadcastType === "event" ? "text-amber-600" : "text-gray-400"}`} />
                  <span className={`font-medium ${broadcastType === "event" ? "text-amber-700" : "text-gray-600"}`}>Event</span>
                </div>
                <div 
                  onClick={() => setBroadcastType("alert")}
                  className={`p-4 border rounded-xl cursor-pointer flex items-center gap-3 transition-colors ${broadcastType === "alert" ? "border-red-500 bg-red-50" : "hover:bg-gray-50"}`}
                >
                  <AlertTriangle className={`w-5 h-5 ${broadcastType === "alert" ? "text-red-600" : "text-gray-400"}`} />
                  <span className={`font-medium ${broadcastType === "alert" ? "text-red-700" : "text-gray-600"}`}>Information / Alert</span>
                </div>
              </div>
            </div>

            {broadcastType === "event" && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Event Name</label>
                  <input
                    type="text"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    placeholder="e.g., Bonfire Night, Live Music"
                    className="w-full p-2.5 border rounded-lg"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Event Time</label>
                  <input
                    type="text"
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                    placeholder="e.g., 8:00 PM today"
                    className="w-full p-2.5 border rounded-lg"
                    required
                  />
                </div>
              </div>
            )}

            {broadcastType === "alert" && (
              <div className="space-y-2 pt-2">
                <label className="text-sm font-semibold text-gray-700">Alert Message</label>
                <textarea
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  placeholder="e.g., Water supply will be off for 2 hours for maintenance."
                  className="w-full p-2.5 border rounded-lg h-32 resize-none"
                  required
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !selectedProperty}
              className="w-full py-6 text-lg font-bold mt-4"
            >
              {loading ? "Sending Broadcast..." : "Send Broadcast Now"} <Send className="w-5 h-5 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};
